import {
  Routes,
  Route,
  Navigate,
  useParams,
  useNavigate
} from "react-router-dom";
import Dashboard from "../pages/Dashboard";
import YardView from "../pages/YardView";
import AllocatedView from "../pages/AllocatedView";
import TimeMachineV2 from "../pages/TimeMachineV2";
import NavQualityHeatmap from "../pages/NavQualityHeatmap";
import TimeMachine from "../components/TimeMachine/TimeMachine";
import { useContext, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  setActivePlantCode,
  setPlantMetaData,
  setPlantTimeZone
} from "../store/plantSlice";
import useSocketConnection from "../hooks/useSocketConnection";
import { AppContext } from "../Context/Context";
import {
  DB_ERROR,
  fetchFromLocalPlantsJSON,
  SOCKET_CHANNELS
} from "../util/helper/constants";
import { useSocketDataEffect } from "../hooks/useSocketData";
import { getAvailablePlant } from "../util/helper/helperFunctions";
import { setAllLgvsOfThisPlant } from "../store/navHeatmapSlice";
import { setShipmentStatus } from "../store/dashboardSlice";
import FeatureChecker from "./FeatureChecker";
import { setYardCapacityData, setYardObjectsRawData } from "../store/yardSlice";
import {
  setAllocatedViewData,
  setIsAllocatedViewLoading
} from "../store/allocatedSlice";
import { setTimeMachineModeIsEnabled } from "../store/oldTimeMachineSlice";

const AppRoutes = () => {
  const { plantCode } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    setSocketConnection,
    setConnectionStatus,
    setPlantConfig,
    socketConnection,
    plantConfig,
    setLocationCode,
    locationsData,
    auth
  } = useContext(AppContext);

  const { socket, connected } = useSocketConnection(plantCode, auth?.accessToken);

  useEffect(() => {
    setSocketConnection(socket);
  }, [socket]);

  useEffect(() => {
    setConnectionStatus(connected);
  }, [connected]);

  useSocketDataEffect({
    socket: socketConnection,
    channel: SOCKET_CHANNELS.PLANT_CONFIG,
    onData: setPlantConfig,
    shouldSubscribe: !fetchFromLocalPlantsJSON
  });

  /*other page socket subcription here in top level 
   as the data are streamed for these are less frequent*/

  useSocketDataEffect({
    socket: socketConnection,
    channel: SOCKET_CHANNELS.YARD_CAPACITY,
    onData: (data) => {
      dispatch(setYardCapacityData(data[0]));
    },
    shouldSubscribe: plantConfig?.yard?.isEnabled
  });

  useSocketDataEffect({
    socket: socketConnection,
    channel: SOCKET_CHANNELS.YARD_OBJECTS,
    onData: (data) => dispatch(setYardObjectsRawData(data)),
    shouldSubscribe: plantConfig?.yard?.isEnabled
  });

  //utilised for the navQheatmap ,lgv list options at level 4
  useSocketDataEffect({
    socket: socketConnection,
    channel: SOCKET_CHANNELS.LGV_LIST,
    onData: (lgvList) => dispatch(setAllLgvsOfThisPlant(lgvList))
  });

  // SHIPMENT STATUS
  useSocketDataEffect({
    socket: socketConnection,
    channel: SOCKET_CHANNELS.SHIPMENT_STATUS,
    onData: (data) => dispatch(setShipmentStatus(data)),
    validate: (data) => data !== DB_ERROR && Array.isArray(data)
  });

  // ALLOCATED VIEW
  useSocketDataEffect({
    socket: socketConnection,
    channel: SOCKET_CHANNELS.ALLOCATED_VIEW,
    onData: (data) => {
      dispatch(setIsAllocatedViewLoading(false));
      dispatch(setAllocatedViewData(data));
    }
  });

  const configHandler = (response) => {
    if (Array.isArray(response) && response.length) {
      dispatch(setPlantMetaData(response));
    }
  };

  useSocketDataEffect({
    socket: socketConnection,
    channel: SOCKET_CHANNELS.CONFIG,
    onData: configHandler
  });

  useEffect(() => {
    const isValidPlant = plantCode && getAvailablePlant(plantCode);

    if (isValidPlant) {
      setLocationCode(plantCode);
      dispatch(setActivePlantCode(plantCode));
    } else {
      navigate("/", { replace: true });
    }

    if (locationsData.length) {
      const plantObject = locationsData?.filter(
        ({ code }) => code === plantCode
      )[0];
      dispatch(setPlantTimeZone(plantObject.timeZone));
    }
    // Reset time machine & marker UI logic
    dispatch(setTimeMachineModeIsEnabled(false));
  }, [plantCode, locationsData]);

  return (
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/yard-view" element={<YardView />} />
      <Route path="/allocated-view" element={<AllocatedView />} />
      <Route path="/old-time-machine" element={<TimeMachine />} />
      <Route
        path="/new-time-machine"
        element={
          <FeatureChecker feature="timeMachine">
            <TimeMachineV2 />
          </FeatureChecker>
        }
      />
      <Route
        path="/nav-quality-heatmap"
        element={
          <FeatureChecker feature="navHeatmap">
            <NavQualityHeatmap />
          </FeatureChecker>
        }
      />
      {/* falls back*/}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRoutes;
